// src/pages/GamePage.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE from '../config';
import './GamePage.css';

const MAGNIFIER_SIZE = 320; // px diameter
const ZOOM = 2.5;

function GamePage() {
  const [additionalInput, setAdditionalInput] = useState('');
  const [imageUrl, setImageUrl] = useState(null);
  const [roundNumber, setRoundNumber] = useState(null);
  const [currentRoundIndex, setCurrentRoundIndex] = useState(null);
  const [hasGuessedThisRound, setHasGuessedThisRound] = useState(false);
  const [userGuessCorrect, setUserGuessCorrect] = useState(null);
  const [submittedGuess, setSubmittedGuess] = useState('');
  const [scientificName, setScientificName] = useState('');
  const [commonName, setCommonName] = useState('');
  const [photoAttribution, setPhotoAttribution] = useState('');
  const [observationLink, setObservationLink] = useState('');
  const [family, setFamily] = useState('');
  const [qualityGrade, setQualityGrade] = useState('');
  const [placeGuess, setPlaceGuess] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [licenseCode, setLicenseCode] = useState('');
  const [speciesObs, setSpeciesObs] = useState(null);
  const [genusTotalObs, setGenusTotalObs] = useState(null);
  const [genusSpeciesCount, setGenusSpeciesCount] = useState(null);
  const [error, setError] = useState(null);
  const [noData, setNoData] = useState(false);
  const [inatUnavailable, setInatUnavailable] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [magnifierEnabled, setMagnifierEnabled] = useState(true);
  const [imageHovered, setImageHovered] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0, imgW: 0, imgH: 0 });
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmNav, setConfirmNav] = useState(null); // null | { destination: string | -1 }
  const imgRef = useRef(null);
  const inputRef = useRef(null);
  const controlsRef = useRef(null);
  const roundActiveRef = useRef(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const maxRounds = parseInt(searchParams.get('rounds'), 10) || 10;
  const sessionId = searchParams.get('session');
  const guessMode = searchParams.get('guessMode') || 'species';
  const gameMode = searchParams.get('mode') || 'genus';
  const alpha = searchParams.get('alpha') || '0.1';
  const commonNameLang = searchParams.get('commonNameLang') || 'en';
  const countryName = searchParams.get('country') || '';
  const placeId = searchParams.get('place_id') || '';
  const nelat = searchParams.get('nelat');
  const swlat = searchParams.get('swlat');
  const nelng = searchParams.get('nelng');
  const swlng = searchParams.get('swlng');
  const hasBbox = nelat && swlat && nelng && swlng;
  const selectedGenera = gameMode === 'genus'
    ? (searchParams.get('genera') || '').split(',').filter(Boolean)
    : [];
  const taxaParam = searchParams.get('taxa') || '';
  const selectedTaxa = (() => {
    if (gameMode !== 'taxa') return [];
    try { return JSON.parse(taxaParam) || []; } catch (_) { return []; }
  })();
  // Names shown in the "Selected …" info panel (genus or taxa modes)
  const selectionList = gameMode === 'taxa'
    ? selectedTaxa.map(t => t.name)
    : selectedGenera;

  const resultsUrl = (() => {
    const base = `/results?session=SESSION&rounds=${maxRounds}&guessMode=${guessMode}&mode=${gameMode}&alpha=${alpha}&commonNameLang=${encodeURIComponent(commonNameLang)}`;
    if (gameMode === 'country') {
      if (hasBbox) {
        const name = countryName ? `&country=${encodeURIComponent(countryName)}` : '';
        return `${base}&nelat=${nelat}&nelng=${nelng}&swlat=${swlat}&swlng=${swlng}${name}`;
      }
      return `${base}&place_id=${placeId}&country=${encodeURIComponent(countryName)}`;
    }
    if (gameMode === 'taxa') {
      return `${base}&taxa=${encodeURIComponent(taxaParam)}`;
    }
    return `${base}&genera=${encodeURIComponent(selectedGenera.join(','))}`;
  })();

  const sourceModeLabel = gameMode === 'country'
    ? hasBbox
      ? (countryName
          ? `By Country (${countryName})`
          : `Custom Region (N${parseFloat(nelat).toFixed(1)}° S${parseFloat(swlat).toFixed(1)}° E${parseFloat(nelng).toFixed(1)}° W${parseFloat(swlng).toFixed(1)}°)`)
      : `By Country${countryName ? ` (${countryName})` : ''}`
    : gameMode === 'taxa'
      ? 'By Taxa'
      : 'By Genus';
  const guessModeLabel = guessMode === 'genus' ? 'Guess Genus' : 'Guess Species';

  const isFinalRound = roundNumber !== null && roundNumber >= maxRounds;
  const isGameFullyDone = isFinalRound && hasGuessedThisRound;

  const STORAGE_KEY = `game_round_${sessionId}`;
  const saveRoundState = (s) => { try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_) {} };
  const loadRoundState = () => { try { const r = sessionStorage.getItem(STORAGE_KEY); return r ? JSON.parse(r) : null; } catch (_) { return null; } };

  const fetchNextImage = async () => {
    if (isFinalRound) return;
    try {
      setIsLoadingImage(true);
      setError(null);

      const imageResponse = await fetch(`${API_BASE}/get_img_url?session_id=${sessionId}`);
      const imageData = await imageResponse.json();

      if (!imageResponse.ok || imageData.error) {
        if (imageData.error === 'no_data') {
          setNoData(true);
        } else if (imageData.error === 'inat_unavailable') {
          setInatUnavailable(true);
          setNoData(true);
        } else {
          setError(imageData.error || 'Failed to fetch image');
        }
        return;
      }

      const fullUrl = `${API_BASE}/${imageData.image_url}`;
      let roundIndex = imageData.round_index;
      if (typeof roundIndex !== 'number') {
        const match = imageData.image_url.match(/round_(\d+)_/);
        roundIndex = match ? parseInt(match[1], 10) : null;
      }

      if (roundIndex === null || Number.isNaN(roundIndex)) {
        setError('Could not determine round index for image.');
        return;
      }

      const roundState = {
        imageUrl: fullUrl,
        currentRoundIndex: roundIndex,
        roundNumber: roundIndex + 1,
        hasGuessedThisRound: false,
        userGuessCorrect: null,
        submittedGuess: '',
        scientificName: '',
        commonName: '',
        speciesObs: null,
        genusTotalObs: null,
        genusSpeciesCount: null,
        photoAttribution: imageData.photo_attribution || '',
        observationLink: imageData.observation_link || '',
        family: imageData.family || '',
        qualityGrade: imageData.quality_grade || '',
        placeGuess: imageData.place_guess || '',
        dateTime: imageData.time_observed_at || imageData.date_time || '',
        licenseCode: imageData.license_code || '',
      };
      saveRoundState(roundState);
      setImageUrl(roundState.imageUrl);
      setCurrentRoundIndex(roundState.currentRoundIndex);
      setRoundNumber(roundState.roundNumber);
      setHasGuessedThisRound(false);
      setUserGuessCorrect(null);
      setSubmittedGuess('');
      setScientificName('');
      setCommonName('');
      setSpeciesObs(null);
      setGenusTotalObs(null);
      setGenusSpeciesCount(null);
      setPhotoAttribution(roundState.photoAttribution);
      setObservationLink(roundState.observationLink);
      setFamily(roundState.family);
      setQualityGrade(roundState.qualityGrade);
      setPlaceGuess(roundState.placeGuess);
      setDateTime(roundState.dateTime);
      setLicenseCode(roundState.licenseCode);
    } catch (err) {
      console.error('Error fetching image:', err);
      setError('Failed to fetch image');
    } finally {
      setIsLoadingImage(false);
    }
  };

  useEffect(() => {
    const saved = loadRoundState();
    if (saved) {
      setImageUrl(saved.imageUrl);
      setCurrentRoundIndex(saved.currentRoundIndex);
      setRoundNumber(saved.roundNumber);
      setHasGuessedThisRound(saved.hasGuessedThisRound);
      setUserGuessCorrect(saved.userGuessCorrect);
      setSubmittedGuess(saved.submittedGuess);
      setScientificName(saved.scientificName);
      setCommonName(saved.commonName);
      setSpeciesObs(saved.speciesObs);
      setGenusTotalObs(saved.genusTotalObs);
      setGenusSpeciesCount(saved.genusSpeciesCount);
      setPhotoAttribution(saved.photoAttribution);
      setObservationLink(saved.observationLink);
      setFamily(saved.family);
      setQualityGrade(saved.qualityGrade);
      setPlaceGuess(saved.placeGuess);
      setDateTime(saved.dateTime);
      setLicenseCode(saved.licenseCode);
    } else {
      fetchNextImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus the guess input whenever a new image loads
  useEffect(() => {
    if (imageUrl) inputRef.current?.focus();
  }, [imageUrl]);

  // Arrow keys cycle focus between enabled controls (input + buttons) in the controls row
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const container = controlsRef.current;
      if (!container) return;
      const focusable = Array.from(
        container.querySelectorAll('input:not(:disabled), button:not(:disabled)')
      );
      if (!focusable.length) return;
      const current = focusable.indexOf(document.activeElement);
      if (e.key === 'ArrowRight') {
        focusable[(current + 1) % focusable.length].focus();
      } else {
        focusable[(current - 1 + focusable.length) % focusable.length].focus();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);


  const submitGuess = async (guess) => {
    if (!imageUrl) { setError('Load an image first before guessing.'); return; }
    if (currentRoundIndex === null) { setError('No round index available.'); return; }
    if (isGameFullyDone) { setError('The game is finished. Please see results.'); return; }

    try {
      setError(null);

      const guessResponse = await fetch(`${API_BASE}/handle_user_guess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_guess: guess, round_index: currentRoundIndex, session_id: sessionId }),
      });

      const guessData = await guessResponse.json();

      if (!guessResponse.ok || guessData.error) {
        setError(guessData.error || 'Failed to submit guess');
        return;
      }

      setSubmittedGuess(guess);
      setUserGuessCorrect(guessData.user_guess_correct);
      setScientificName(guessData.scientific_name);
      setCommonName(guessData.common_name);
      setSpeciesObs(guessData.species_observations ?? null);
      setGenusTotalObs(guessData.genus_total_observations ?? null);
      setGenusSpeciesCount(guessData.genus_species_count ?? null);
      setHasGuessedThisRound(true);
      setError(null);
      setAdditionalInput('');
      const saved = loadRoundState();
      if (saved) saveRoundState({
        ...saved,
        hasGuessedThisRound: true,
        userGuessCorrect: guessData.user_guess_correct,
        submittedGuess: guess,
        scientificName: guessData.scientific_name,
        commonName: guessData.common_name,
        speciesObs: guessData.species_observations ?? null,
        genusTotalObs: guessData.genus_total_observations ?? null,
        genusSpeciesCount: guessData.genus_species_count ?? null,
      });
    } catch (err) {
      console.error('Error submitting user guess:', err);
      setError('Failed to submit guess');
    }
  };

  const handleSubmitGuess = (e) => { e.preventDefault(); submitGuess(additionalInput); };
  const handleGiveUp = () => submitGuess('');

  const roundActive = roundNumber !== null && !isGameFullyDone;
  roundActiveRef.current = roundActive;

  const safeNavigate = (destination) => {
    if (roundActive) setConfirmNav({ destination });
    else navigate(destination);
  };

  const endSessionAndNavigate = (destination) => {
    try { sessionStorage.removeItem(`game_round_${sessionId}`); } catch (_) {}
    fetch(`${API_BASE}/end_session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    }).catch(() => {});
    if (destination === -1) window.history.go(-2);
    else navigate(destination);
  };

  // Intercept browser back button
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      if (roundActiveRef.current) {
        window.history.pushState(null, '', window.location.href);
        setConfirmNav({ destination: -1 });
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleMouseMove = (e) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    setMagnifierPos({ x: e.clientX - rect.left, y: e.clientY - rect.top, imgW: rect.width, imgH: rect.height });
  };

  const handleImageClick = () => { setIsExpanded(v => !v); };

  const disableFetchButton = isLoadingImage || isFinalRound || (!hasGuessedThisRound && roundNumber !== null);
  const disableGuessInput = hasGuessedThisRound || isGameFullyDone;
  const disableGuessButton = hasGuessedThisRound || isGameFullyDone;
  const disableSubmitButton = disableGuessButton || !additionalInput.trim();

  const licenseInfo = (code) => {
    if (!code) return null;
    const map = {
      'cc0':          { label: 'CC0 (Public Domain)', url: 'https://creativecommons.org/publicdomain/zero/1.0/' },
      'cc-by':        { label: 'CC BY',               url: 'https://creativecommons.org/licenses/by/4.0/' },
      'cc-by-nc':     { label: 'CC BY-NC',            url: 'https://creativecommons.org/licenses/by-nc/4.0/' },
      'cc-by-sa':     { label: 'CC BY-SA',            url: 'https://creativecommons.org/licenses/by-sa/4.0/' },
      'cc-by-nc-sa':  { label: 'CC BY-NC-SA',         url: 'https://creativecommons.org/licenses/by-nc-sa/4.0/' },
      'cc-by-nd':     { label: 'CC BY-ND',            url: 'https://creativecommons.org/licenses/by-nd/4.0/' },
      'cc-by-nc-nd':  { label: 'CC BY-NC-ND',         url: 'https://creativecommons.org/licenses/by-nc-nd/4.0/' },
    };
    return map[code.toLowerCase()] || { label: code, url: null };
  };

  const authorName = (() => {
    if (!photoAttribution) return '';
    const match = photoAttribution.match(/^\(c\)\s+(.+?),/);
    return match ? match[1] : photoAttribution;
  })();

  if (noData) {
    return (
      <div className="game-page">
        <div className="game-header">
          <button className="game-title-btn" onClick={() => navigate('/')}>
            <img src="/logo.png?v=2" alt="" className="header-logo" />
            PlantID Game
          </button>
        </div>
        <div className="no-data-message">
          {inatUnavailable ? (
            <>
              <h2>iNaturalist is unavailable</h2>
              <p>
                iNaturalist, where all plant observations come from, is temporarily
                unavailable (e.g. for maintenance). Please try again later.
              </p>
            </>
          ) : (
            <>
              <h2>No data available</h2>
              <p>
                No plant observations could be found for the selected genera.
                Please go back and choose different ones.
              </p>
            </>
          )}
          <button className="submit-button" onClick={() => navigate('/settings?session=' + sessionId)}>
            Back to settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-page">

      {/* Title — top left, clicks back to landing page */}
      <div className="game-header">
        <button className="game-title-btn" onClick={() => safeNavigate('/')}>
          <img src="/logo.png?v=2" alt="" className="header-logo" />
          PlantID Game
        </button>
      </div>

      {/* Game mode label */}
      <p style={{ textAlign: 'center', fontSize: '1.25rem', fontWeight: '600', color: 'var(--moss-dark)', margin: '10px 0' }}>
        {sourceModeLabel} &mdash; {guessModeLabel}
      </p>

      {/* Round counter — centered */}
      {roundNumber && (
        <div className="round-display">
          <div className="round-dots">
            {Array.from({ length: maxRounds }, (_, i) => (
              <span
                key={i}
                className={
                  'round-dot' +
                  (i < roundNumber - 1 ? ' done' : '') +
                  (i === roundNumber - 1 ? ' current' : '')
                }
              />
            ))}
          </div>
          <p className="round-label">{roundNumber} / {maxRounds}</p>
        </div>
      )}

      {/* Main content: info panel | image | spacer */}
      <div className="main-content">

        {/* Info panel — always visible, content only after guessing */}
        <div className="info-panel">
          <p className="info-panel-title">Additional information</p>
          {hasGuessedThisRound && (
            <>
              {commonName && (
                <div className="info-row">
                  <span className="info-label">Common name</span>
                  <span className="info-value">{commonName}</span>
                </div>
              )}
              {scientificName && (
                <div className="info-row">
                  <span className="info-label">Scientific name</span>
                  <span className="info-value info-scientific">{scientificName}</span>
                </div>
              )}
              {family && (
                <div className="info-row">
                  <span className="info-label">Family</span>
                  <span className="info-value info-scientific">{family}</span>
                </div>
              )}
              {(speciesObs != null && genusTotalObs != null) || genusSpeciesCount != null ? (
                <div className="info-row">
                  <span className="info-label">Statistics</span>
                  <span className="info-value">
                    {speciesObs != null && genusTotalObs != null && (
                      <>
                        Species obs: {speciesObs.toLocaleString()}<br />
                        Genus obs: {genusTotalObs.toLocaleString()}<br />
                        Percentage: {(speciesObs / genusTotalObs * 100).toFixed(2)}%<br />
                      </>
                    )}
                    {genusSpeciesCount != null && <>Num of species in genus: {genusSpeciesCount.toLocaleString()}</>}
                  </span>
                </div>
              ) : null}
              {qualityGrade && (
                <div className="info-row">
                  <span className="info-label">Obs. quality</span>
                  <span className="info-value">{qualityGrade.replace(/_/g, ' ')}</span>
                </div>
              )}
              {placeGuess && (
                <div className="info-row">
                  <span className="info-label">Location</span>
                  <span className="info-value">{placeGuess}</span>
                </div>
              )}
              {dateTime && (
                <div className="info-row">
                  <span className="info-label">Time</span>
                  <span className="info-value">{dateTime}</span>
                </div>
              )}
              {observationLink && (
                <div className="info-row">
                  <span className="info-label">Observation</span>
                  <span className="info-value">
                    <a href={observationLink} target="_blank" rel="noopener noreferrer">iNaturalist link</a>
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Image area */}
        <div className="image-area">
          {imageUrl ? (
            <div className="image-container">
              <div
                className={`image-wrapper${!magnifierEnabled ? ' magnifier-off' : ''}`}
                onMouseEnter={() => setImageHovered(true)}
                onMouseLeave={() => setImageHovered(false)}
                onMouseMove={handleMouseMove}
                onClick={handleImageClick}
                style={{ cursor: magnifierEnabled ? 'none' : (isExpanded ? 'zoom-out' : 'zoom-in') }}
              >
                <img ref={imgRef} src={imageUrl} alt="Plant" className="plant-image" />
                {magnifierEnabled && imageHovered && (
                  <div
                    className="magnifier"
                    style={{
                      left: magnifierPos.x - MAGNIFIER_SIZE / 2,
                      top: magnifierPos.y - MAGNIFIER_SIZE / 2,
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: `${magnifierPos.imgW * ZOOM}px ${magnifierPos.imgH * ZOOM}px`,
                      backgroundPosition: `-${magnifierPos.x * ZOOM - MAGNIFIER_SIZE / 2}px -${magnifierPos.y * ZOOM - MAGNIFIER_SIZE / 2}px`,
                    }}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="image-container image-placeholder">
              <p>{isLoadingImage ? 'Loading image...' : error || 'No image loaded.'}</p>
            </div>
          )}
        </div>

        {/* Lightbox overlay */}
        {isExpanded && imageUrl && (
          <div className="lightbox-overlay" onClick={() => setIsExpanded(false)}>
            <img src={imageUrl} alt="Plant enlarged" className="lightbox-img" />
          </div>
        )}

        {/* Right column — Game Information box */}
        <div className="game-info-panel">
          <p className="info-panel-title">Game Information</p>

          {gameMode !== 'country' && (
            <div className="info-row">
              <span className="info-label">{gameMode === 'taxa' ? 'Selected Taxa' : 'Selected Genera'}</span>
              {selectionList.length > 0
                ? selectionList.map((g, i) => (
                    <span key={i} className="info-value info-scientific">{g}</span>
                  ))
                : <span className="info-value">—</span>
              }
            </div>
          )}

          <div className="info-row" style={{ marginTop: 'auto' }}>
            <span className="info-label">Settings</span>
            <label className={`magnifier-switch-label${magnifierEnabled ? ' is-on' : ''}`} style={{ marginTop: '6px' }}>
              Magnifying glass
              <span className="magnifier-switch-track" />
              <input
                type="checkbox"
                checked={magnifierEnabled}
                onChange={() => setMagnifierEnabled(v => !v)}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Photo attribution and license below the main content row */}
      {authorName && (
        <p className="photo-attribution">
          photo by {authorName}
          {licenseCode && (() => {
            const info = licenseInfo(licenseCode);
            return info ? (
              <>
                {' · '}
                {info.url
                  ? <a href={info.url} target="_blank" rel="noopener noreferrer" className="license-link">{info.label}</a>
                  : <span>{info.label}</span>
                }
              </>
            ) : null;
          })()}
        </p>
      )}

      {/* Controls row — aligned with image width */}
      <div className="controls-outer">
        <div className="side-spacer" />
        <form onSubmit={handleSubmitGuess} className="controls-row" ref={controlsRef}>
          <input
            type="text"
            value={additionalInput}
            onChange={(e) => setAdditionalInput(e.target.value)}
            placeholder={guessMode === 'genus' ? 'Enter genus name...' : 'Enter species name...'}
            className="guess-input"
            ref={inputRef}
            disabled={disableGuessInput}
          />
          <button type="submit" className="submit-button" disabled={disableSubmitButton}>
            Submit
          </button>
          <button type="button" className="giveup-button" onClick={handleGiveUp} disabled={disableGuessButton}>
            I don't know
          </button>
          <button type="button" className="fetch-button" onClick={fetchNextImage} disabled={disableFetchButton}>
            {isLoadingImage ? 'Loading...' : 'Next round'}
          </button>
          <button type="button" className="results-button" onClick={() => safeNavigate(resultsUrl.replace('SESSION', sessionId))}>
            See results
          </button>
        </form>
        <div className="side-spacer" />
      </div>

      {/* Guess result — aligned with image width, with column headers */}
      {hasGuessedThisRound && (
        <div className="result-outer">
          <div className="side-spacer" />
          <div className="result-content">
            <div className="column-headers">
              <span className="col-header">Your guess</span>
              <span className="col-header">Actual</span>
              <span className="col-header">Result</span>
            </div>
            <div className="guess-result-row">
              <span className="guess-result-cell guess-result-user">
                {submittedGuess ? submittedGuess : <em>no guess</em>}
              </span>
              <span className="guess-result-cell guess-result-correct">
                {guessMode === 'genus' ? scientificName.split(' ')[0] : scientificName}
              </span>
              <span className={`guess-result-cell ${userGuessCorrect ? 'verdict-correct' : 'verdict-incorrect'}`}>
                {userGuessCorrect ? 'Correct' : 'Incorrect'}
              </span>
            </div>
          </div>
          <div className="side-spacer" />
        </div>
      )}

      {error && (
        <div className="error-section">
          <p style={{ color: 'red' }}>Error: {error}</p>
        </div>
      )}

      {/* Navigation warning modal */}
      {confirmNav && (
        <div className="lightbox-overlay" style={{ cursor: 'default' }} onClick={() => setConfirmNav(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg, #fff)',
              borderRadius: '12px',
              padding: '32px 40px',
              textAlign: 'center',
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
              minWidth: '300px',
            }}
          >
            <p style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--moss-dark)', marginBottom: '8px' }}>
              Warning
            </p>
            <p style={{ color: 'var(--text-dark)', marginBottom: '24px' }}>
              This will end the current round.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="fetch-button" onClick={() => endSessionAndNavigate(confirmNav.destination)}>
                Continue
              </button>
              <button className="giveup-button" onClick={() => setConfirmNav(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GamePage;
